import React, { useMemo, useCallback, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { X, Loader2, ShieldCheck } from "lucide-react";

// Load Stripe once — loadStripe is idempotent-safe but caching here avoids
// re-parsing on every re-mount of the modal.
const STRIPE_PROMISE = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/**
 * Renders Stripe's Embedded Checkout inside a full-screen dark-overlay modal.
 *
 * Props:
 *   clientSecret : string   — from backend session.create_embedded_session
 *   title        : string   — modal header (e.g. "Pay & send invites")
 *   subtitle     : string   — optional line under the header
 *   onComplete   : ({sessionId}) => void  — fired once Stripe confirms the
 *                  form was submitted successfully. Parent should verify the
 *                  final payment_status via a backend status poll before
 *                  running any side effects.
 *   onClose      : () => void — fired when the user cancels/closes.
 *   testid       : string   — root wrapper data-testid
 */
export default function EmbeddedStripeCheckoutModal({
  clientSecret,
  title = "Complete payment",
  subtitle,
  onComplete,
  onClose,
  testid = "stripe-checkout-modal",
}) {
  const [firedComplete, setFiredComplete] = useState(false);

  const options = useMemo(() => {
    if (!clientSecret) return null;
    return {
      clientSecret,
      onComplete: () => {
        if (firedComplete) return;
        setFiredComplete(true);
        onComplete?.({ clientSecret });
      },
    };
  }, [clientSecret, firedComplete, onComplete]);

  const stopBubble = useCallback((e) => e.stopPropagation(), []);

  if (!clientSecret) return null;

  return (
    <div
      className="fixed inset-0 z-[300] flex items-start justify-center bg-slate-950/70 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid={testid}
    >
      <div
        onClick={stopBubble}
        className="w-full max-w-lg mt-6 mb-6 rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
      >
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between bg-gradient-to-r from-blue-50 to-cyan-50">
          <div className="min-w-0">
            <div className="text-[14px] font-bold text-slate-900 truncate">{title}</div>
            {subtitle && <div className="text-[11.5px] text-slate-600 truncate">{subtitle}</div>}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid={`${testid}-close`}
            className="h-8 w-8 rounded-full hover:bg-white/60 flex items-center justify-center shrink-0"
            title="Close"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>

        <div className="p-2 sm:p-3 bg-slate-50/40 max-h-[75vh] overflow-y-auto" data-testid={`${testid}-inner`}>
          <EmbeddedCheckoutProvider stripe={STRIPE_PROMISE} options={options}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </div>

        <div className="px-5 py-2.5 border-t border-slate-100 flex items-center justify-between text-[11.5px] text-slate-500 bg-white">
          <div className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
            Secured by Stripe · your card details never touch our servers
          </div>
          {firedComplete && (
            <div className="inline-flex items-center gap-1.5 text-emerald-600 font-semibold">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Confirming…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
